import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Download, PackageSearch, ChevronRight, Loader2, Boxes, CheckCircle2, GitCommitHorizontal, Rocket,
} from 'lucide-react';
import { importAbapCode, formatApiError, parseImportTicketTitle } from '@/lib/abapImportApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { fetchTicketSummaries } from '@/lib/ticketsApi';

const API = INCIDENTS_API;
const TRANSPORTS_POLL_MS = 8000;

const ABAP_WORKFLOW_STAGE_META = {
  imported: { label: 'Imported', className: 'bg-slate-500 text-white', icon: Boxes, card: 'ticket-card--default' },
  validated: { label: 'Syntax OK', className: 'bg-cp-mod text-cp-fg', icon: CheckCircle2, card: 'ticket-card--test' },
  validation_failed: { label: 'Syntax Failed', className: 'bg-cp-crit text-white', icon: GitCommitHorizontal, card: 'ticket-card--failed' },
  atc_clean: { label: 'ATC Clean', className: 'bg-cp-ok text-white', icon: CheckCircle2, card: 'ticket-card--done' },
  atc_partial: { label: 'ATC Partial', className: 'bg-cp-imp text-white', icon: GitCommitHorizontal, card: 'ticket-card--deploy' },
  validated_and_deployed: { label: 'Deployed', className: 'bg-emerald-600 text-white', icon: Rocket, card: 'ticket-card--done' },
};

function stageMeta(stage) {
  return ABAP_WORKFLOW_STAGE_META[stage] || ABAP_WORKFLOW_STAGE_META.imported;
}

function isImportTicket(ticket) {
  const type = (ticket?.customization_type || ticket?.category || '').trim().toLowerCase();
  return type === 'import';
}

export default function AbapCodeTransports() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('tickets', 'create');

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [programName, setProgramName] = useState('');

  useEffect(() => {
    fetchTransports();
    const interval = setInterval(fetchTransports, TRANSPORTS_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const fetchTransports = async () => {
    try {
      const page = await fetchTicketSummaries(API, { limit: 50, offset: 0, includeTotal: false });
      setTickets(page.items.filter(isImportTicket));
    } catch (error) {
      console.error('Error fetching ABAP transports:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = tickets.length;
    const deployed = tickets.filter((t) => t.abap_workflow_stage === 'validated_and_deployed').length;
    const pending = total - deployed;
    return { total, deployed, pending };
  }, [tickets]);

  const handleImport = async () => {
    if (!canCreate) {
      toast.error(permissionDeniedTitle(false, 'import ABAP code'));
      return;
    }
    const name = programName.trim();
    if (!name) {
      toast.error('Enter a program name to import');
      return;
    }
    setImporting(true);
    try {
      const result = await importAbapCode({ programName: name });
      toast.success(`Imported ${result.object_name} into ${result.ticket_number}`);
      setImportOpen(false);
      setProgramName('');
      await fetchTransports();
      navigate(`/abap-transports/${result.ticket_number}`);
    } catch (error) {
      console.error('Error importing code:', error);
      toast.error(formatApiError(error));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 animate-in fade-in duration-300">
        <Loader2 className="h-8 w-8 animate-spin text-cp-violet" />
        <p className="text-sm text-muted-foreground">Loading transports…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="abap-transports-page">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm transition-transform hover:scale-105">
              <PackageSearch className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">ABAP Code Transports</h1>
              <p className="text-muted-foreground mt-1">
                Import existing SAP programs, review a diff, then syntax-check / ATC / deploy them.
              </p>
            </div>
          </div>
        </div>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogTrigger asChild>
            <span title={permissionDeniedTitle(canCreate, 'import ABAP code')}>
              <Button disabled={!canCreate} data-testid="import-code-button" className="gap-2">
                <Download className="h-4 w-4" />
                Import Code
              </Button>
            </span>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Code</DialogTitle>
              <DialogDescription>
                Fetch an existing SAP program's source and create a new transport ticket for it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="import-program-name">Program name</Label>
              <Input
                id="import-program-name"
                placeholder="e.g. Z_PURCHASE_ORDER_REPORT"
                value={programName}
                onChange={(e) => setProgramName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                data-testid="import-program-name-input"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button onClick={handleImport} disabled={importing || !programName.trim()} data-testid="import-code-submit">
                {importing ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing…</>) : 'Import'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {tickets.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="transition-shadow hover:shadow-md">
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Total Imports</div>
              <div className="text-2xl font-bold mt-1">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="transition-shadow hover:shadow-md">
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Deployed</div>
              <div className="text-2xl font-bold mt-1 text-emerald-600">{stats.deployed}</div>
            </CardContent>
          </Card>
          <Card className="transition-shadow hover:shadow-md">
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">In Progress</div>
              <div className="text-2xl font-bold mt-1 text-cp-imp">{stats.pending}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <PackageSearch className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              No ABAP transports yet. Import an existing SAP program to get started.
            </p>
            <span title={permissionDeniedTitle(canCreate, 'import ABAP code')}>
              <Button onClick={() => setImportOpen(true)} disabled={!canCreate}>
                <Download className="mr-2 h-4 w-4" />
                Import Code
              </Button>
            </span>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tickets.map((ticket, index) => {
            const { objectName, objectType } = parseImportTicketTitle(ticket.title);
            const meta = stageMeta(ticket.abap_workflow_stage);
            const StageIcon = meta.icon;
            return (
              <Card
                key={ticket.id}
                className={`group ticket-card ${meta.card} relative overflow-hidden cursor-pointer transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-px hover:shadow-md animate-in fade-in slide-in-from-bottom-1`}
                style={{ animationDelay: `${Math.min(index, 8) * 40}ms`, animationDuration: '250ms', animationFillMode: 'backwards' }}
                onClick={() => navigate(`/abap-transports/${ticket.ticket_number}`)}
                data-testid={`transport-card-${ticket.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                    <Badge className={`text-xs gap-1 ${meta.className}`}>
                      <StageIcon className="h-3 w-3" />
                      {meta.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-lg truncate" title={objectName}>{objectName}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">{objectType} &middot; {ticket.sap_system}</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1 transition-transform group-hover:translate-x-0.5" />
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    Imported {new Date(ticket.created_at).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
